import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import { repostCentralFillOrder } from 'store/thunks/workQueueThunks';
import { MARK_DELIVERED_ELIGIBLE_STATUSES } from '../constants/centralFillEligibility';

import RepostCentralFillDialog from './RepostCentralFillDialog';
import MarkCentralFillOrderDeliveredDialog from './MarkCentralFillOrderDeliveredDialog';

// Statuses that represent a recoverable failure and can be reposted to the pharmacy.
// Deliberately EXCLUDES 'MISSING_REASON_FOR_COMPOUNDING' — that has its own resolve flow.
const REPOSTABLE_STATUSES = [
  'SUBMIT_FAILED',
  'CLINIC_NOT_CONFIGURED',
  'CLINIC_AUTOCREATE_FAILED',
  'PROVIDER_RESOLUTION_FAILED',
  'PATIENT_RESOLUTION_FAILED',
  'PRODUCT_NOT_MAPPED',
  'EXCEPTION',
];

const CentralFillQueueOptions = ({
  orderId,
  trackingId,
  status,
  patientName,
  drugName,
  compoundingPharmacyName,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [repostDialogOpen, setRepostDialogOpen] = useState(false);
  const [markDeliveredDialogOpen, setMarkDeliveredDialogOpen] = useState(false);
  const [isReposting, setIsReposting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const menuOpen = !!anchorEl;

  const isRepostable = REPOSTABLE_STATUSES.includes(status);
  const canMarkDelivered = !!trackingId && MARK_DELIVERED_ELIGIBLE_STATUSES.has(status);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleViewDetails = () => {
    handleMenuClose();
    navigate(`/orders/${orderId}`);
  };

  const handleMarkDeliveredOpen = () => {
    handleMenuClose();
    setMarkDeliveredDialogOpen(true);
  };

  const handleRepostOpen = () => {
    handleMenuClose();
    setRepostDialogOpen(true);
  };

  const handleRepostClose = () => {
    if (isReposting) return;
    setRepostDialogOpen(false);
  };

  const handleRepostConfirm = () => {
    setIsReposting(true);
    dispatch(repostCentralFillOrder({ trackingId }))
      .then((response) => {
        if (response?.reposted) {
          enqueueSnackbar('Order requeued to the compounding pharmacy', { variant: 'success' });
        } else {
          enqueueSnackbar(response?.message || 'Order could not be reposted', { variant: 'info' });
        }
        setRepostDialogOpen(false);
      })
      .catch(() => {
        // Error already surfaced via handleError in the thunk.
        setRepostDialogOpen(false);
      })
      .finally(() => setIsReposting(false));
  };

  return (
    <>
      <IconButton
        id={`WorkQueue-CentralFillQueue-${orderId}-options-button`}
        aria-label={`Compounding queue options ${orderId}`}
        aria-controls={menuOpen ? `WorkQueue-CentralFillQueue-${orderId}-options-menu` : undefined}
        aria-haspopup='true'
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={handleMenuOpen}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id={`WorkQueue-CentralFillQueue-${orderId}-options-menu`}
        aria-labelledby={`WorkQueue-CentralFillQueue-${orderId}-options-button`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        {isRepostable && <MenuItem onClick={handleRepostOpen}>Repost</MenuItem>}
        {canMarkDelivered && (
          <MenuItem onClick={handleMarkDeliveredOpen}>Mark as Delivered</MenuItem>
        )}
      </Menu>

      {canMarkDelivered && (
        <MarkCentralFillOrderDeliveredDialog
          open={markDeliveredDialogOpen}
          onClose={() => setMarkDeliveredDialogOpen(false)}
          trackingId={trackingId}
          orderId={orderId}
          patientName={patientName}
          drugName={drugName}
          compoundingPharmacyName={compoundingPharmacyName}
          status={status}
        />
      )}

      <RepostCentralFillDialog
        open={repostDialogOpen}
        onClose={handleRepostClose}
        orderId={orderId}
        onConfirm={handleRepostConfirm}
        isLoading={isReposting}
      />
    </>
  );
};

CentralFillQueueOptions.propTypes = {
  orderId: PropTypes.string.isRequired,
  trackingId: PropTypes.string,
  status: PropTypes.string,
  patientName: PropTypes.string,
  drugName: PropTypes.string,
  compoundingPharmacyName: PropTypes.string,
};

CentralFillQueueOptions.defaultProps = {
  trackingId: undefined,
  status: undefined,
  patientName: undefined,
  drugName: undefined,
  compoundingPharmacyName: undefined,
};

export default CentralFillQueueOptions;
